import { ErrorOutlined } from "@mui/icons-material";
import { Autocomplete, Box, Button, Menu, type SxProps, styled, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/system";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";
import type React from "react";
import type { ReactNode } from "react";
import { type Dispatch, type FC, type FormEventHandler, type SetStateAction, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { INK, STATUS, STATUS_BADGE } from "../../theme/tokens";
import {
	adjustNightShiftDate,
	cloneDateTimePreservingZone,
	getEffectiveDayStart,
	getShiftDisplayDate,
	isBeforeDayStart,
	parseTimeString,
	resolveEndTimeForStartChange,
} from "../../utils/dateUtils";
import type { NurseRole } from "../../utils/models";
import { isShiftDurationBelowHours } from "../../utils/openShiftDuration";
import { buildShiftOptionWindows, groupShiftOptionWindowsByLength } from "../../utils/shiftOptionWindows";
import { resolveShiftTimeOptions } from "../../utils/shiftTimeOptions";
import { matchesTypedTime, parseTypedTimeOfDay } from "../../utils/typedTimeOfDay";
import { EditTimeTouchFields } from "./EditTimeTouchFields";
import { EditTimeTouchLengths } from "./EditTimeTouchLengths";
import { EditTimeTouchWindows } from "./EditTimeTouchWindows";

const StyledTextField = styled(TextField)({
	"& .MuiInputBase-root": { height: "40px" },
	"& .MuiInputBase-input": { fontSize: "0.875rem", fontWeight: 500 },
	"& .MuiInputLabel-root": {
		fontSize: "1rem",
		fontWeight: 400,
		color: alpha(INK.SECONDARY, 0.5),
	},
});

export const TimeRangeSelection: FC<{
	initStartTimeWDefault: DateTime;
	selectedStart: DateTime;
	setSelectedStart: Dispatch<SetStateAction<DateTime>>;
	selectedEnd: DateTime;
	setSelectedEnd: Dispatch<SetStateAction<DateTime>>;
	hoursLock?: number;
	minStartTime?: DateTime;
	maxEndTime?: DateTime;
	disabled?: boolean;
	disableAutoCompetePortal?: boolean;
	/** Override the default "from"/"to" field labels (e.g. "Start"/"End"). */
	startLabel?: string;
	endLabel?: string;
	/** data-testid stamped on the start/end input elements. */
	startDataTestId?: string;
	endDataTestId?: string;
	/** Punch editing uses the clock-in buffer; schedule editing uses the scheduled grace. */
	isActual?: boolean;
	/** Paint both fields in the error state; the caller renders the message beside them. */
	error: boolean;
	/**
	 * Layout overrides for the From/To pair's container — e.g. a container query that stacks the pair in a
	 * narrow side panel on a wide screen, where the built-in viewport breakpoint keeps them side by side.
	 */
	sx: SxProps<Theme> | null;
	/**
	 * `picker` draws From/To as time pickers that open a clock rather than the keyboard (`EditTimeTouchFields`), for
	 * a phone sheet. Absent = the typed fields with their half-hour lists.
	 */
	fieldVariant?: "typed" | "picker";
}> = ({
	initStartTimeWDefault,
	selectedStart,
	setSelectedStart,
	selectedEnd,
	setSelectedEnd,
	hoursLock,
	minStartTime,
	maxEndTime,
	disabled = false,
	disableAutoCompetePortal,
	startLabel,
	endLabel,
	startDataTestId,
	endDataTestId,
	isActual = false,
	error,
	sx,
	// DEFAULT: typed — every caller but the phone sheets' touch editor keeps the fields it has today.
	fieldVariant = "typed",
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const graceMinutes = isActual ? chosenDepartment.shift_delta_minutes : chosenDepartment.scheduled_shift_delta_minutes;

	const staffingDateRef = useMemo(
		// adjustNightShiftDate expects the staffing-day reference, not the wall-clock start time.
		// For PM dayStart, a shift starting at 8PM Apr 13 belongs to staffing day Apr 14.
		() => getShiftDisplayDate(initStartTimeWDefault, chosenDepartment.dayStart, graceMinutes),
		[initStartTimeWDefault, chosenDepartment.dayStart, graceMinutes],
	);
	const createTimeAndUpdateDay = useCallback(
		(newValue: DateTime): DateTime => adjustNightShiftDate(newValue, staffingDateRef, chosenDepartment.dayStart, graceMinutes),
		[staffingDateRef, chosenDepartment.dayStart, graceMinutes],
	);

	// Where a time of day lands, for the typed fields and the touch pickers alike. A start: on the shift's staffing day,
	// then moved across midnight by the department's day start. An end: on the start's day, rolled to the next day when
	// it would fall before the start.
	const placeStartTime = useCallback(
		(hour: number, minute: number): DateTime =>
			adjustNightShiftDate(staffingDateRef.startOf("day").set({ hour, minute }), staffingDateRef, chosenDepartment.dayStart, graceMinutes),
		[staffingDateRef, chosenDepartment.dayStart, graceMinutes],
	);
	const placeEndTime = useCallback(
		(hour: number, minute: number): DateTime => {
			const end = selectedStart.startOf("day").set({ hour, minute });
			return end < selectedStart ? end.plus({ day: 1 }) : end;
		},
		[selectedStart],
	);

	const parseAutocompleteValue = useCallback(
		(value: string, isEndTime = false): DateTime | undefined => {
			// AM/PM is resolved into the hour before set() to avoid DST gap issues (e.g. setting hour=2 on
			// spring-forward day makes Luxon advance to hour=3, causing a later PM adjustment to land 1h late).
			const typed = parseTypedTimeOfDay(value);
			if (!typed) return;
			return isEndTime ? placeEndTime(typed.hour, typed.minute) : placeStartTime(typed.hour, typed.minute);
		},
		[placeStartTime, placeEndTime],
	);

	const startTimes = useMemo<DateTime[]>(() => {
		const times: DateTime[] = [];
		const baseDate = initStartTimeWDefault.startOf("day");
		// Open at the effective day start, not the raw one: at 07:00 with 2h grace the list starts at
		// 05:00, so the times that stay on this day are the first ones you see.
		const { hour, minute } = parseTimeString(getEffectiveDayStart(chosenDepartment.dayStart, graceMinutes));
		const start = baseDate.set({ hour, minute });

		// Generate exactly 48 half-hour slots (full 24h) starting from the effective day start
		for (let i = 0; i < 48; i++) {
			times.push(start.plus({ minutes: i * 30 }));
		}

		return times.filter((t) => !minStartTime || t > minStartTime).filter((t) => !maxEndTime || t < maxEndTime);
	}, [initStartTimeWDefault, maxEndTime, minStartTime, chosenDepartment.dayStart, graceMinutes]);

	const endTimes = useMemo<DateTime[]>(() => {
		const times: DateTime[] = [];
		// Options are the next 24h in 30min increments
		for (let i = 1; i < 48; i++) {
			times.push(selectedStart.plus({ minutes: i * 30 }));
		}

		return times.filter((t) => !maxEndTime || t < maxEndTime);
	}, [selectedStart, maxEndTime]);

	const scrollListboxToIndex = (targetIndex: number) => {
		setTimeout(() => {
			const listbox = document.querySelector('[role="listbox"]');
			const options = listbox?.querySelectorAll('[role="option"]');
			if (listbox && options && options.length > targetIndex) {
				const target = options[targetIndex] as HTMLElement;
				const offset = target.offsetTop - listbox.clientHeight / 2 + target.clientHeight / 2;
				listbox.scrollTop = offset;
			}
		}, 0);
	};

	const filterTimeOptions = useCallback(
		(options: DateTime[], { inputValue }: { inputValue: string }) => {
			if (!inputValue) return options;
			return options.filter((option) => matchesTypedTime(option, inputValue));
		},
		[],
	);

	const updateStartTime = useCallback(
		(newStart: DateTime): void => {
			setSelectedStart(newStart);
			setSelectedEnd((previousEnd) => resolveEndTimeForStartChange(previousEnd, newStart, hoursLock));
		},
		[hoursLock, setSelectedEnd, setSelectedStart],
	);

	// Above the picker branch's early return, so the two field variants call the same hooks in the same order.
	const startFieldId = useId();
	const endFieldId = useId();

	if (fieldVariant === "picker") {
		return (
			<EditTimeTouchFields
				selectedStart={selectedStart}
				selectedEnd={selectedEnd}
				onStartPicked={(time) => updateStartTime(placeStartTime(time.hour, time.minute))}
				onEndPicked={(time) => setSelectedEnd(placeEndTime(time.hour, time.minute))}
				startLabel={startLabel ?? t("from")}
				endLabel={endLabel ?? t("to")}
				startDataTestId={startDataTestId}
				endDataTestId={endDataTestId}
				disabled={disabled}
				sx={sx}
			/>
		);
	}

	return (
		<Box
			sx={[
				(theme) => ({
					display: "flex",
					justifyContent: "stretch",
					gap: 2,
					[theme.breakpoints.down("md")]: { flexDirection: "column" },
				}),
				...(sx === null ? [] : Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Autocomplete
				fullWidth
				handleHomeEndKeys
				id={startFieldId}
				freeSolo
				options={startTimes}
				filterOptions={filterTimeOptions}
				getOptionLabel={(option) => {
					const dayOption = typeof option === "string" ? DateTime.fromISO(option).setZone(timezone) : option;
					return dayOption.toFormat("h:mm a");
				}}
				renderInput={(params) => (
					<StyledTextField
						{...params}
						label={startLabel ?? t("from")}
						error={error}
						slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps?.htmlInput, "data-testid": startDataTestId } }}
					/>
				)}
				value={selectedStart}
				onChange={(event, newValue) => {
					if (typeof newValue === "string") {
						const newTime = parseAutocompleteValue(newValue, false);
						if (newTime) updateStartTime(newTime);
					} else if (DateTime.isDateTime(newValue)) {
						const newTime = createTimeAndUpdateDay(newValue);
						updateStartTime(newTime);
					}
					/** HACK: This is required to force the "To" selection to refresh its state */
					setSelectedEnd(cloneDateTimePreservingZone);

					if (disableAutoCompetePortal) {
						event.stopPropagation();
						event.preventDefault();
					}
				}}
				disablePortal={disableAutoCompetePortal}
				onFocus={(event) => {
					(event.target as HTMLInputElement).select();
				}}
				onBlur={(event) => {
					const { value } = event.target as HTMLInputElement;
					if (!value) {
						/** HACK: This is required to force the "From" selection to refresh its state */
						setSelectedStart(cloneDateTimePreservingZone);
						return;
					}

					const newValue = parseAutocompleteValue(value, false);
					if (newValue) {
						updateStartTime(newValue);
						/** HACK: This is required to force the "To" selection to refresh its state */
						setSelectedEnd(cloneDateTimePreservingZone);
					}
				}}
				disabled={disabled}
				onOpen={() => {
					let targetIndex = 0;
					if (selectedStart) {
						targetIndex = findClosestIndexLE(startTimes, selectedStart);
					}
					if (startTimes.length > targetIndex) {
						if (startTimes[targetIndex] === selectedStart) {
							setSelectedStart(startTimes[targetIndex]);
						}
						scrollListboxToIndex(targetIndex);
					}
				}}
			/>
			<Autocomplete
				fullWidth
				handleHomeEndKeys
				id={endFieldId}
				freeSolo
				options={endTimes}
				filterOptions={filterTimeOptions}
				getOptionLabel={(option) => {
					const dayOption = typeof option === "string" ? DateTime.fromISO(option).setZone(timezone) : option;
					const diff = dayOption.diff(selectedStart, "minutes").minutes;
					const timeFmt = dayOption.toFormat("h:mm a");

					return `${timeFmt} (${Math.floor(diff / 60)}h${(Math.abs(diff) % 60).toFixed(0)}m)`;
				}}
				renderInput={(params) => (
					<StyledTextField
						{...params}
						label={endLabel ?? t("to")}
						error={error}
						slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps?.htmlInput, "data-testid": endDataTestId } }}
					/>
				)}
				value={selectedEnd}
				onChange={(event, newValue) => {
					let newTime = typeof newValue === "string" ? parseAutocompleteValue(newValue, true) : newValue?.startOf("minute");
					if (newTime) {
						if (newTime < selectedStart) {
							newTime = newTime.plus({ day: 1 });
						}
						setSelectedEnd(newTime);
					}
					setSelectedStart(cloneDateTimePreservingZone);
					if (disableAutoCompetePortal) {
						event.stopPropagation();
						event.preventDefault();
					}
				}}
				disablePortal={disableAutoCompetePortal}
				onFocus={(event) => {
					(event.target as HTMLInputElement).select();
				}}
				onBlur={(event) => {
					const { value } = event.target as HTMLInputElement;
					if (!value) {
						/** HACK: This is required to force the "To" selection to refresh its state */
						setSelectedEnd(cloneDateTimePreservingZone);
						return;
					}

					let newTime = parseAutocompleteValue(value, true);
					if (newTime) {
						if (newTime < selectedStart) {
							newTime = newTime.plus({ day: 1 });
						}
						setSelectedEnd(newTime);
					}
				}}
				onOpen={() => {
					let targetIndex = 23; // 12h after beginning
					if (selectedEnd) {
						targetIndex = findClosestIndexLE(endTimes, selectedEnd);
					}
					if (0 <= targetIndex && targetIndex < endTimes.length) {
						if (endTimes[targetIndex] === selectedEnd) {
							setSelectedEnd(endTimes[targetIndex]);
						}
						scrollListboxToIndex(targetIndex);
					}
				}}
				disabled={disabled}
			/>
		</Box>
	);
};

const NightShiftDateNote: FC<{ selectedStart: DateTime; dayStart: string; graceMinutes: number }> = ({ selectedStart, dayStart, graceMinutes }) => {
	const { t } = useTranslation();
	// Test the effective boundary, not the raw day start. Inside the grace window the shift stays on
	// this day, so the note would be telling the user the opposite of what happens.
	const boundary = getEffectiveDayStart(dayStart, graceMinutes);
	if (!isBeforeDayStart(selectedStart, boundary)) return null;
	const { hour, minute } = parseTimeString(boundary);
	const isAmDayStart = hour * 60 + minute <= 12 * 60;
	const date = selectedStart.toFormat("M/d");
	return (
		<Typography sx={{ color: STATUS_BADGE.LILAC_INK, fontSize: "12px", fontStyle: "italic" }}>
			{t(isAmDayStart ? "night.shift.next.day" : "night.shift.previous.day", { date })}
		</Typography>
	);
};

type EditTimePopoverProps = {
	startTime?: DateTime;
	endTime?: DateTime;
	minStartTime?: DateTime;
	maxEndTime?: DateTime;
	onTimeChange: (_: { newStartTime: DateTime; newEndTime: DateTime }) => void;
	onClose: (event: React.MouseEvent<HTMLElement> | React.FormEvent<HTMLElement>) => void;
	anchorEl: HTMLElement | null;
	isActual?: boolean;
	role?: `${NurseRole}` | "default";
	/**
	 * The job this shift is scheduled under. In job-managed departments it takes precedence over
	 * `role` for both option lists (DAT-580); departments still on roles ignore it. Optional
	 * throughout — a call site with no job in scope keeps the role behavior.
	 */
	jobId?: string;
	/**
	 * If provided, will use these role groups to determine full shift times instead of the passed role
	 */
	roleGroups?: string[];
	justUpdateOnSave?: boolean;
	menuWrapper?: boolean;
	fullsize?: boolean;
	hidePatialShiftLengths?: boolean;
	hideFullShifts?: boolean;
	disableAutoCompetePortal?: boolean;
	/**
	 * This is used in case we don't have a chosen department context like in float pool or multi facility
	 */
	specificDepartmentId?: string;
	/**
	 * When true, hides the footer (Close + Apply) and instead streams every valid time change
	 * to the parent via onTimeChange. Use for inline embedding where the parent owns the commit action.
	 */
	hideActions?: boolean;
	/** Optional content rendered between the shift-length options and the footer (e.g. the agency post delay). */
	extraContent?: ReactNode;
	/**
	 * `touch` restyles the editor for a finger inside a phone sheet — upright length labels and touch-sized chips, with
	 * the window the editor holds marked by value, and From/To as time pickers that open a clock rather than the
	 * keyboard (APP-886) — and changes nothing else (the notes, the footer). Absent = the desktop editor, unchanged.
	 */
	appearance?: "default" | "touch";
	/**
	 * Apply stays disabled while start equals end. For callers that turn the window into a real shift (Add Staff),
	 * where a zero-length window would publish an empty shift. Absent = today's rule (start ≤ end).
	 */
	requireNonZeroLength?: boolean;
	/** Optional exclusive elapsed-duration limit. Omit to preserve ordinary scheduling behavior. */
	maximumDurationHours?: number;
	sx?: SxProps<Theme>;
};
export const EditTimePopover: FC<EditTimePopoverProps> = ({
	startTime: initStartTime,
	endTime: initEndTime,
	onTimeChange,
	onClose,
	anchorEl,
	role = "default",
	jobId,
	menuWrapper = true,
	minStartTime,
	fullsize = false,
	maxEndTime,
	hidePatialShiftLengths = false,
	hideFullShifts = false,
	disableAutoCompetePortal,
	specificDepartmentId,
	roleGroups,
	hideActions = false,
	extraContent,
	appearance,
	// DEFAULT: false keeps every existing caller on the start ≤ end rule; only Add Staff opts in.
	requireNonZeroLength = false,
	maximumDurationHours,
	sx,
	isActual = false,
}) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	const departmentToUse = useMemo(
		() => (specificDepartmentId ? userInfo.departments.find((dept) => dept.department_id === specificDepartmentId) : chosenDepartment) ?? chosenDepartment,
		[specificDepartmentId, userInfo.departments, chosenDepartment],
	);
	// Punch editing uses the clock-in buffer; schedule editing uses the scheduled grace.
	const graceMinutes = isActual ? departmentToUse.shift_delta_minutes : departmentToUse.scheduled_shift_delta_minutes;
	// Job row → role key → default. See shiftTimeOptions.ts. The flag evaluates for the chosen
	// department, which is `departmentToUse` at every call site today — `specificDepartmentId` is
	// declared but never passed. Wiring it up would need a per-department flag value.
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { fullShiftTimes, partialBlockShifts } = useMemo(
		() => resolveShiftTimeOptions({ department: departmentToUse, jobManagementEnabled, jobId, role, roleGroups }),
		[departmentToUse, jobManagementEnabled, jobId, role, roleGroups],
	);
	const initStartTimeWDefault = useMemo<DateTime>(() => initStartTime ?? DateTime.now(), [initStartTime]);
	const initEndTimeWDefault = useMemo<DateTime>(() => {
		if (!initEndTime) return DateTime.now();
		return initEndTime < initStartTimeWDefault ? initEndTime.plus({ day: 1 }) : initEndTime;
	}, [initEndTime, initStartTimeWDefault]);

	const [hoursLock, setHoursLock] = useState<number>();

	const staffingDateRef = useMemo(
		() => getShiftDisplayDate(initStartTimeWDefault, chosenDepartment.dayStart, graceMinutes),
		[initStartTimeWDefault, chosenDepartment.dayStart, graceMinutes],
	);

	const fullShiftTimeGroups = useMemo(
		() =>
			groupShiftOptionWindowsByLength(
				buildShiftOptionWindows({ fullShiftTimes, staffingDate: staffingDateRef, dayStart: chosenDepartment.dayStart, graceMinutes }).filter(
					({ start, end }) => maximumDurationHours === undefined || isShiftDurationBelowHours(start, end, maximumDurationHours),
				),
			),
		[fullShiftTimes, staffingDateRef, chosenDepartment.dayStart, graceMinutes, maximumDurationHours],
	);
	const allowedPartialBlockShifts = useMemo(
		() => partialBlockShifts.filter((hours) => maximumDurationHours === undefined || hours < maximumDurationHours),
		[partialBlockShifts, maximumDurationHours],
	);

	const [selectedStart, setSelectedStart] = useState<DateTime>(initStartTimeWDefault);
	const [selectedEnd, setSelectedEnd] = useState<DateTime>(initEndTimeWDefault);

	const isZeroLength = selectedStart.toMillis() === selectedEnd.toMillis();
	// Only the zero-length rule gets its own message; every other invalid window keeps the generic one.
	const isBlockedZeroLength = requireNonZeroLength && isZeroLength;
	const isDurationTooLong = maximumDurationHours !== undefined && !isShiftDurationBelowHours(selectedStart, selectedEnd, maximumDurationHours);
	const areSelectedTimesValid =
		selectedStart <= selectedEnd &&
		!isBlockedZeroLength &&
		!isDurationTooLong &&
		!(minStartTime && selectedStart < minStartTime) &&
		!(maxEndTime && selectedEnd > maxEndTime);

	const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault();
			if (!areSelectedTimesValid) return;
			onTimeChange({
				newStartTime: selectedStart,
				newEndTime: selectedEnd,
			});
			onClose(e);
		},
		[areSelectedTimesValid, selectedStart, selectedEnd, onTimeChange, onClose],
	);

	// In hideActions mode the parent owns the commit action and needs every valid change immediately,
	// since there is no internal Apply button to submit through. Skip the initial render so we don't
	// echo the parent's own seed values back at it.
	const didMount = useRef(false);
	useEffect(() => {
		if (!hideActions) return;
		if (!didMount.current) {
			didMount.current = true;
			return;
		}
		if (!areSelectedTimesValid) return;
		onTimeChange({ newStartTime: selectedStart, newEndTime: selectedEnd });
	}, [hideActions, selectedStart, selectedEnd, areSelectedTimesValid, onTimeChange]);

	const body = (
		<Box
			component="form"
			sx={[
				{
					p: 2,
					display: "flex",
					flexDirection: "column",
					width: fullsize ? "100%" : "fit-content",
				},
				(theme) => ({ [theme.breakpoints.up("md")]: { minWidth: "450px" } }),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
			onSubmit={onSubmit}
			onKeyDown={(event) => {
				if (event.key === "Tab") {
					event.stopPropagation();
				}
			}}
		>
			{!hideFullShifts && !!fullShiftTimeGroups.length && appearance === "touch" && (
				<EditTimeTouchWindows
					groups={fullShiftTimeGroups}
					selectedStart={selectedStart}
					selectedEnd={selectedEnd}
					onPick={(start, end) => {
						setSelectedStart(start);
						setSelectedEnd(end);
					}}
				/>
			)}
			{!hideFullShifts && !!fullShiftTimeGroups.length && appearance !== "touch" && (
				<>
					<Typography
						component="h2"
						sx={{
							padding: 0,
							margin: 0,
							marginBottom: "0.375rem",
							fontWeight: 400,
							color: alpha(INK.SECONDARY, 0.5),
						}}
					>
						{t("choose.shift")}
					</Typography>
					<Box
						component="ul"
						sx={{
							padding: 0,
							margin: 0,
							marginBottom: "1.5rem",
							pb: "1rem",
							listStyle: "none",
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}
					>
						{fullShiftTimeGroups.map(({ lengthBucketHours: shiftLength, windows: shifts }) => {
							return (
								<Box
									component="li"
									key={shiftLength}
									sx={{
										display: "flex",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: 1,
									}}
								>
									<Typography
										component="h5"
										sx={{
											p: 0,
											m: 0,
											width: "2rem",
											justifySelf: "flex-start",
											transform: "rotate(-90deg)",
											transformOrigin: "center",
											color: alpha(INK.SECONDARY, 0.5),
											fontWeight: "600",
											textAlign: "center",
										}}
									>
										{shiftLength}H
									</Typography>
									<Box
										component="ul"
										sx={(theme) => ({
											m: 0,
											p: 0,
											listStyle: "none",
											display: "flex",
											gap: 1,
											overflowX: "auto",
											[theme.breakpoints.down("md")]: {
												// On mobile we should wrap the cards
												overflowX: "visible",
												flexWrap: "wrap",
											},
										})}
									>
										{shifts.map(({ start: startTime, end: endTime }, index) => {
											const isSelected = startTime === selectedStart && endTime === selectedEnd;
											return (
												<Box component="li" key={index.toString()} sx={{ flexShrink: 0 }}>
													<Button
														onClick={() => {
															setSelectedStart(startTime);
															setSelectedEnd(endTime);
														}}
														variant={isSelected ? "contained" : "outlined"}
														sx={{
															width: "fit-content",
															px: "10px",
															py: "5px",
															fontWeight: "600",
															whiteSpace: "nowrap",
														}}
													>
														{startTime.toFormat("h:mm a")} - {endTime.toFormat("h:mm a")}
													</Button>
												</Box>
											);
										})}
									</Box>
								</Box>
							);
						})}
					</Box>
				</>
			)}

			<TimeRangeSelection
				initStartTimeWDefault={initStartTimeWDefault}
				selectedStart={selectedStart}
				setSelectedStart={setSelectedStart}
				selectedEnd={selectedEnd}
				setSelectedEnd={setSelectedEnd}
				hoursLock={hoursLock}
				minStartTime={minStartTime}
				maxEndTime={maxEndTime}
				disableAutoCompetePortal={disableAutoCompetePortal}
				isActual={isActual}
				error={false}
				sx={null}
				fieldVariant={appearance === "touch" ? "picker" : "typed"}
			/>

			<NightShiftDateNote selectedStart={selectedStart} dayStart={departmentToUse.dayStart} graceMinutes={graceMinutes} />

			{!isDurationTooLong && Math.abs(selectedEnd.diff(selectedStart, "hours").as("hours")) > 16 && (
				<Typography
					sx={{
						color: STATUS.ERROR.main,
						fontSize: "12px",
						display: "flex",
						gap: 1,
						alignItems: "center",
					}}
				>
					<ErrorOutlined sx={{ width: "14px", height: "14px" }} />
					{t("shift.duration.too.long")}
				</Typography>
			)}
			{!areSelectedTimesValid && (
				<Typography
					sx={{
						color: STATUS.ERROR.main,
						fontSize: "12px",
						display: "flex",
						gap: 1,
						alignItems: "center",
					}}
				>
					<ErrorOutlined sx={{ width: "14px", height: "14px" }} />
					{t(isDurationTooLong ? "error.open.shift.duration.too.long" : isBlockedZeroLength ? "edit.time.zero.length" : "ensure.valid.times")}
				</Typography>
			)}

			{!hidePatialShiftLengths && !!allowedPartialBlockShifts.length && appearance === "touch" && (
				<EditTimeTouchLengths
					lengths={allowedPartialBlockShifts}
					selectedLength={selectedEnd.diff(selectedStart, "hours").as("hours")}
					onPick={(shiftLength) => {
						setHoursLock((lock) => (lock === shiftLength ? undefined : shiftLength));
						setSelectedEnd(selectedStart.plus({ hours: shiftLength }));
					}}
				/>
			)}
			{!hidePatialShiftLengths && !!allowedPartialBlockShifts.length && appearance !== "touch" && (
				<>
					<Typography
						component="h2"
						sx={{
							padding: 0,
							margin: 0,
							marginTop: "1.5rem",
							marginBottom: "0.375rem",
							fontWeight: 400,
							color: alpha(INK.SECONDARY, 0.5),
						}}
					>
						{t("choose.shift.duration")}
					</Typography>
					<Box
						component="ul"
						sx={{
							p: 0,
							m: 0,
							display: "flex",
							flexWrap: "wrap",
							gap: 2,
							listStyle: "none",
						}}
					>
						{allowedPartialBlockShifts.map((shiftLength, index) => {
							const isSelected = selectedEnd.diff(selectedStart, "hours").as("hours") === shiftLength;
							return (
								<Box component="li" key={index.toString()}>
									<Button
										onClick={() => {
											setHoursLock((lock) => (lock === shiftLength ? undefined : shiftLength));
											setSelectedEnd(selectedStart.plus({ hours: shiftLength }));
										}}
										variant={isSelected ? "contained" : "outlined"}
										sx={{
											borderRadius: "999px",
											height: "36px",
											width: "36px",
											fontWeight: isSelected ? "600" : "500",
										}}
									>
										{shiftLength}
									</Button>
								</Box>
							);
						})}
					</Box>
				</>
			)}

			{extraContent}

			{!hideActions && (
				<Box
					component="footer"
					sx={{
						mt: "2rem",
						pt: "1rem",
						display: "flex",
						justifyContent: "flex-end",
						gap: 2,
						borderTop: "1px solid #4D557926",
					}}
				>
					<Button
						onClick={(event) => {
							//allow the parent to handle the close and pass the event
							onClose(event);
						}}
						variant="outlined"
					>
						{t("close")}
					</Button>
					<Button
						type="submit"
						variant="contained"
						sx={{ bgcolor: STATUS_BADGE.BRAND_FALLBACK, "&:hover": { bgcolor: STATUS_BADGE.LILAC_INK } }}
						disabled={!areSelectedTimesValid}
					>
						{t("apply")}
					</Button>
				</Box>
			)}
		</Box>
	);

	if (menuWrapper) {
		return (
			<Menu open onClose={onClose} anchorEl={anchorEl}>
				<LocalizationProvider dateAdapter={AdapterLuxon}>{body}</LocalizationProvider>
			</Menu>
		);
	}

	return body;
};

function findClosestIndexLE(arr: DateTime[], target: DateTime): number {
	return Math.max((arr.findIndex((item) => item > target) ?? arr.length) - 1, 0);
}
